import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  acceptTerms,
  addAccount,
  authSwitchAccountNew,
  checkSession,
  getAccountList,
  getReviews,
  loginWeb,
  logOutWeb,
  removeAccount,
  switchAccount,
  verifyLoginDetails,
} from "store/services/session/auth";
import { closeCenterModal } from "store/slices/CenterModal.slice";
import { openToast } from "store/slices/toast.slice";
import { getApiMessage } from "utils/apiErrorCode";

export const VerifyLoginDetailsAction = createAsyncThunk(
  "auth/verifyLoginDetails",
  async (payload, { rejectWithValue, fulfillWithValue, dispatch }) => {
    try {
      let { onSuccess, onError, ...rest } = payload;
      const res = await verifyLoginDetails(rest);
      if (res.status === 1) {
        onSuccess?.();
        return fulfillWithValue(res);
      }
      onError?.(getApiMessage(res?.statusCode));
      return rejectWithValue(res);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const CheckSessionAction = createAsyncThunk(
  "web/checkSession ",
  async (payload, { rejectWithValue, fulfillWithValue }) => {
    try {
      const res = await checkSession(payload);
      if (res.status === 1) {
        return fulfillWithValue(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const LoginWebAction = createAsyncThunk(
  "web/login",
  async (payload, { rejectWithValue, fulfillWithValue, dispatch }) => {
    try {
      let { onSuccess, onError, ...rest } = payload;
      const res = await loginWeb(rest);
      if (res.status === 1) {
        onSuccess?.(res);
        return fulfillWithValue(res);
      }
      onError?.(res);
      return rejectWithValue(res);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const AcceptTermsAction = createAsyncThunk(
  "web/acceptTerms",
  async (_, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const res = await acceptTerms();
      if (res.status === 1) {
        dispatch(closeCenterModal());
        return fulfillWithValue(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const LogOutAction = createAsyncThunk(
  "web/logOut",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      let { onSuccess, ...rest } = payload;
      const res = await logOutWeb(rest);
      if (res.status === 1) {
        dispatch(closeCenterModal());
        onSuccess?.();
        return fulfillWithValue(res);
      }
      dispatch(
        openToast({
          type: "error",
          message: getApiMessage(res?.statusCode),
        }),
      );
      return rejectWithValue(res);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const GetAccountListAction = createAsyncThunk(
  "web/getAccountList",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const res = await getAccountList(payload);
      if (res.status === 1) {
        // if any async operation need to be done on response data, do it here,
        // then full fill

        return fulfillWithValue(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const AddAccountAction = createAsyncThunk(
  "web/addAccount",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      let { onSuccess, onError, ...rest } = payload;
      const res = await addAccount(rest);
      if (res.status === 1) {
        if (onSuccess) {
          onSuccess(res?.response?.two_factor_enabled);
        }
        return fulfillWithValue(res);
      }
      if (onError) {
        onError(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const SwitchAccountAction = createAsyncThunk(
  "web/switchAccount",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const res = await switchAccount(payload);
      if (res.status === 1) {
        // if any async operation need to be done on response data, do it here,
        // then full fill

        return fulfillWithValue(res);
      }
      dispatch(
        openToast({
          type: "error",
          message: res?.statusCode
            ? getApiMessage(res?.statusCode)
            : res?.message,
        }),
      );
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const RemoveAccountAction = createAsyncThunk(
  "web/removeAccount",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    const { onSuccess, ...rest } = payload;
    try {
      const res = await removeAccount(rest);
      if (res.status === 1) {
        onSuccess?.();
        dispatch(closeCenterModal());
        return fulfillWithValue({ rest });
      }
      return rejectWithValue(res);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const GetReviewsAction = createAsyncThunk(
  "web/getReviews",
  async (payload, { rejectWithValue, fulfillWithValue }) => {
    try {
      const res = await getReviews();
      if (res.status === 1) {
        // if any async operation need to be done on response data, do it here,
        // then full fill

        return fulfillWithValue(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      if (payload?.onError) {
        payload?.onError(
          error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        );
      }
      return rejectWithValue(error);
    }
  },
);

export const AuthSwitchAccountNewAction = createAsyncThunk(
  "web/authSwitchAccountNew",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const res = await authSwitchAccountNew(payload);
      if (res.status === 1) {
        // if any async operation need to be done on response data, do it here,
        // then full fill

        return fulfillWithValue(res);
      }
      return rejectWithValue({ message: getApiMessage(res?.statusCode) });
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);
